import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetSports, apiCreateSport, apiUpdateSport } from '../services/api';
import { SPORT, UI } from '../constants/colors';
import { SPORT_LABELS, sportEmoji } from '../constants/sports';
import { useFormDate } from '../utils/formDate';
import InfoButton from '../components/InfoButton';
import './FormSport.css';

/* ===== Form icons / emojis ===== */
import pasEssoufflee from '../assets/forms/form_emojis/petite.png';
import moderee from '../assets/forms/form_emojis/moderee.png';
import tresEssoufflee from '../assets/forms/form_emojis/angoissee.png';

import { ReactComponent as DropletG } from '../assets/forms/form_emojis/dropletg.svg';
import { ReactComponent as HeartG } from '../assets/forms/form_emojis/heartg.svg';
import { ReactComponent as HeartbeatW } from '../assets/forms/form_emojis/heartbeatw.svg';
import { ReactComponent as HeartbeatB } from '../assets/forms/form_emojis/heartbeatb.svg';
import { ReactComponent as CroissantB } from '../assets/forms/form_emojis/croissantb.svg';
import { ReactComponent as Trophy } from '../assets/forms/form_emojis/trophy.svg';
import { ReactComponent as Warning } from '../assets/forms/form_emojis/warning.svg';
import { ReactComponent as EnergyB } from '../assets/forms/form_emojis/energyb.svg';
import { ReactComponent as EssoufflementIcon } from '../assets/forms/form_emojis/essouflement.svg';
import courbaturesImg from '../assets/forms/form_emojis/courbatures.png';

const ESSOUFFLEMENT_MAP = {
  'pas-essoufflee': 'Pas essoufflée',
  moderee: 'Modérée',
  'tres-essoufflee': 'Très essoufflée',
};

// Courbatures : le délai d'apparition (24 à 48 h) désigne la séance responsable, ce
// qu'un simple « j'ai des courbatures » ne dit pas. Champ de journée, pas de séance :
// c'est un état du corps hérité d'un entraînement passé (cf. schemas/sport.py).
const COURBATURES_ONSET_MAP = {
  aucune: 'Aucune',
  veille: 'De la veille',
  'avant-veille': "De l'avant-veille",
  'les-deux': 'Les deux',
};

const COURBATURES_LEVEL_MAP = {
  legeres: 'Légères',
  moderees: 'Modérées',
  fortes: 'Fortes',
};

// Lignes cliquables, dans le langage du sélecteur d'essoufflement : le `<select>` natif
// qu'elles remplacent était le seul du formulaire, et demandait deux gestes là où les
// autres champs en demandent un. Les libellés viennent de la map d'envoi plutôt que
// d'une seconde liste — deux sources se seraient désynchronisées au premier renommage.
const COURBATURES_ONSET_OPTIONS = [
  { id: 'aucune', emoji: '👍', sub: "Pas de courbatures aujourd'hui" },
  { id: 'veille', emoji: '😬', sub: 'Apparues ~24 h après la séance' },
  { id: 'avant-veille', emoji: '😖', sub: 'Apparues ~48 h après la séance' },
  { id: 'les-deux', emoji: '😵', sub: 'Deux séances encore dans les jambes' },
].map((o) => ({ ...o, label: COURBATURES_ONSET_MAP[o.id] }));

// Reverse maps for pre-population
const ESSOUFFLEMENT_REV = Object.fromEntries(Object.entries(ESSOUFFLEMENT_MAP).map(([k, v]) => [v, k]));
const COURBATURES_ONSET_REV = Object.fromEntries(Object.entries(COURBATURES_ONSET_MAP).map(([k, v]) => [v, k]));
const COURBATURES_LEVEL_REV = Object.fromEntries(Object.entries(COURBATURES_LEVEL_MAP).map(([k, v]) => [v, k]));

// Une journée peut compter plusieurs séances (cf. schemas/sport.py).
const MAX_SEANCES = 6;

const emptySeance = () => ({
  sports: [],
  rpe: 1,
  fcEffort: '',
  essoufflement: null,
  isCompetition: false,
  note: '',
});

const toApiSeance = (s) => ({
  sports_pratiques: s.sports.length ? s.sports : null,
  rpe: parseInt(s.rpe, 10),
  effort_heart_rate: s.fcEffort ? parseInt(s.fcEffort, 10) : null,
  breathlessness: s.essoufflement ? ESSOUFFLEMENT_MAP[s.essoufflement] : null,
  is_competition: s.isCompetition,
  notes: s.note || null,
});

const FormSport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuth();
  // Jour édité : `?date=` s'il est fourni (rattrapage depuis le calendrier), sinon
  // aujourd'hui. Ce formulaire écrivait « aujourd'hui » en dur, quel que soit le jour
  // sélectionné — la saisie rétroactive était donc impossible côté sport.
  const { selectedDate, isBackfill, label: dateLabel, withDate } = useFormDate();
  const [error, setError] = useState('');
  const [existingId, setExistingId] = useState(null);

  /* ===== State pour chaque section ===== */
  const [jourReposSportif, setJourReposSportif] = useState(false);
  const [jourCompetition, setJourCompetition] = useState(false);
  const [blessure, setBlessure] = useState(false);
  const [descriptionBlessure, setDescriptionBlessure] = useState('');
  const [rpe, setRpe] = useState(1);
  const [fcRepos, setFcRepos] = useState('');
  const [fcEffort, setFcEffort] = useState('');
  const [essoufflement, setEssoufflement] = useState(null);
  const [courbaturesOnset, setCourbaturesOnset] = useState(null);
  const [courbaturesLevel, setCourbaturesLevel] = useState(null);
  const [sportsPratiques, setSportsPratiques] = useState([]);
  const [note, setNote] = useState('');
  // Séances au-delà de la première (bouton « Ajouter une séance »).
  const [seancesSup, setSeancesSup] = useState([]);
  // Sport ponctuel : pratiqué aujourd'hui sans figurer dans les sports du profil.
  const [sportPonctuel, setSportPonctuel] = useState('');
  const [showPonctuel, setShowPonctuel] = useState(false);

  // Pré-remplissage à partir du relevé du jour édité, s'il existe déjà
  useEffect(() => {
    if (!user?.id) return;
    apiGetSports(user.id, getToken()).then((entries) => {
      const entry = entries.find((e) => e.date === selectedDate);
      if (!entry) return;
      setExistingId(entry.id);
      setJourReposSportif(entry.is_rest_day ?? false);
      setBlessure(entry.has_injury ?? false);
      setDescriptionBlessure(entry.injury_description ?? '');
      setFcRepos(entry.rest_heart_rate != null ? String(entry.rest_heart_rate) : '');
      // Donnée de journée : elle se relit sur l'entrée, jamais sur une séance.
      setCourbaturesOnset(entry.soreness_onset ? (COURBATURES_ONSET_REV[entry.soreness_onset] ?? null) : null);
      setCourbaturesLevel(entry.soreness_level ? (COURBATURES_LEVEL_REV[entry.soreness_level] ?? null) : null);

      // Quand la journée a été enregistrée séance par séance, on repart du
      // détail : les champs de plus haut niveau ne sont que le résumé du jour
      // (RPE le plus dur, union des sports…), les recharger tels quels
      // écraserait la première séance.
      const sessions = entry.sessions ?? [];
      const first = sessions[0];
      setJourCompetition((first ? first.is_competition : entry.is_competition) ?? false);
      setRpe((first ? first.rpe : entry.rpe) ?? 1);
      const fcEffortValue = first ? first.effort_heart_rate : entry.effort_heart_rate;
      setFcEffort(fcEffortValue != null ? String(fcEffortValue) : '');
      const breath = first ? first.breathlessness : entry.breathlessness;
      setEssoufflement(breath ? (ESSOUFFLEMENT_REV[breath] ?? null) : null);
      setSportsPratiques((first ? first.sports_pratiques : entry.sports_pratiques) ?? []);
      setNote((first ? first.notes : entry.notes) ?? '');

      setSeancesSup(sessions.slice(1).map((s) => ({
        sports: s.sports_pratiques ?? [],
        rpe: s.rpe ?? 1,
        fcEffort: s.effort_heart_rate != null ? String(s.effort_heart_rate) : '',
        essoufflement: s.breathlessness ? (ESSOUFFLEMENT_REV[s.breathlessness] ?? null) : null,
        isCompetition: s.is_competition ?? false,
        note: s.notes ?? '',
      })));
    }).catch(() => {});
  }, [user?.id, getToken, selectedDate]);

  const addSportPonctuel = () => {
    const label = sportPonctuel.trim();
    if (!label) return;
    setSportPonctuel('');
    setSportsPratiques((prev) =>
      prev.some((s) => s.toLowerCase() === label.toLowerCase()) ? prev : [...prev, label]
    );
  };

  const updateSeance = (index, patch) =>
    setSeancesSup((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const handleSubmit = async () => {
    setError('');

    // Une journée de repos n'a pas de séance.
    const seances = jourReposSportif ? [] : [
      toApiSeance({ sports: sportsPratiques, rpe, fcEffort, essoufflement, isCompetition: jourCompetition, note }),
      ...seancesSup.map(toApiSeance),
    ];

    // Résumé du jour porté par les champs de plus haut niveau : la séance la
    // plus dure donne le RPE, la FC effort et l'essoufflement ; les sports sont
    // l'union des séances. Sur une journée à une seule séance, cela redonne
    // exactement les valeurs saisies.
    const rpes = seances.map((s) => s.rpe).filter((v) => v != null);
    const summaryRpe = rpes.length ? Math.max(...rpes) : null;
    const hardest = seances.find((s) => s.rpe === summaryRpe);
    const fcs = seances.map((s) => s.effort_heart_rate).filter((v) => v != null);
    const summarySports = [...new Set(seances.flatMap((s) => s.sports_pratiques ?? []))];

    const payload = {
      date: selectedDate,
      is_rest_day: jourReposSportif,
      is_competition: seances.some((s) => s.is_competition),
      has_injury: blessure,
      injury_description: blessure && descriptionBlessure ? descriptionBlessure : null,
      rpe: jourReposSportif ? null : summaryRpe,
      rest_heart_rate: fcRepos ? parseInt(fcRepos, 10) : null,
      effort_heart_rate: fcs.length ? Math.max(...fcs) : null,
      breathlessness: hardest?.breathlessness ?? null,
      // Hors `seances` : les courbatures décrivent la journée, pas une séance — et un
      // jour de repos sportif peut parfaitement en compter.
      soreness_onset: courbaturesOnset ? COURBATURES_ONSET_MAP[courbaturesOnset] : null,
      soreness_level: courbaturesLevel ? COURBATURES_LEVEL_MAP[courbaturesLevel] : null,
      sports_pratiques: summarySports.length ? summarySports : null,
      notes: note || null,
      // Le détail n'est transmis que s'il y a vraiment plusieurs séances :
      // les journées classiques gardent le payload d'avant.
      sessions: seances.length > 1 ? seances : null,
    };
    try {
      let sportResponse;
      if (existingId) {
        const { date: _, ...payloadWithoutDate } = payload;
        sportResponse = await apiUpdateSport(existingId, payloadWithoutDate, getToken());
      } else {
        sportResponse = await apiCreateSport(payload, getToken());
      }
      // Ouvert depuis « Ajouter mon sport » : seul ce volet était à saisir.
      if (location.state?.solo) { navigate('/accueil'); return; }
      navigate(withDate('/formulaire/confirmation'), { state: { ...location.state, sportData: sportResponse } });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="form-sport">
      {/* ===== Header ===== */}
      <div className="form-sport__header">
        <div className="form-sport__header-top">
          <div className="form-sport__header-left">
            <div className="form-sport__header-logo">
              <HeartbeatW className="form-sport__header-icon" />
            </div>
            <div>
              <h1 className="form-sport__header-title">Performance & ressenti</h1>
              {/* En rattrapage, le jour édité doit être visible : sans ça, rien ne
                  distingue à l'écran la saisie du jour de celle d'un jour passé. */}
              <p className="form-sport__header-sub">
                {isBackfill ? `Saisie pour ${dateLabel}` : "Comment s'est passée ton activité ?"}
              </p>
            </div>
          </div>
          <button className="form-sport__close" onClick={() => navigate('/accueil')} aria-label="Fermer">✕</button>
        </div>
        {/* Onglets catégories */}
        <div className="form-sport__tabs-wrapper">
          <div className="form-sport__tabs">
              <button className="form-sport__tab" onClick={() => navigate(withDate('/formulaire/cycle'))}>
                <DropletG className="form-sport__tab-icon" /> Cycle
              </button>
              <button className="form-sport__tab" onClick={() => navigate(withDate('/formulaire/mental'))}>
                <HeartG className="form-sport__tab-icon" /> Mental
              </button>
              <button className="form-sport__tab form-sport__tab--active">
                <HeartbeatW className="form-sport__tab-icon" /> Sport
              </button>
          </div>
        </div>
      </div>

      {/* ===== Jour de repos sportif ===== */}
      <div className="form-sport__section form-sport__section--bordered form-sport__section--rest">
        <div className="form-sport__section-header">
          <CroissantB className="form-sport__section-icon croissant" />
          <div>
            <h3 className="form-sport__section-title">Jour de repos sportif</h3>
            <p className="form-sport__section-sub">Pas d'activité sportive</p>
          </div>
        </div>
        <label className="form-sport__switch">
          <input type="checkbox" checked={jourReposSportif} onChange={() => setJourReposSportif(!jourReposSportif)} />
          <span className="form-sport__switch-slider" />
        </label>
      </div>

      {/* ===== Blessure =====
          Placée juste après le jour de repos, et **jamais masquée** : une blessure est
          précisément un motif de repos, et c'est le jour où l'on ne s'entraîne pas
          qu'on a le plus besoin de la signaler. */}
      <div className="form-sport__section form-sport__section--bordered form-sport__section--injury">
        <div className="form-sport__section-header">
          <Warning className="form-sport__section-icon warning" />
          <div>
            <h3 className="form-sport__section-title">Blessure<InfoButton infoKey="formBlessure" /></h3>
            <p className="form-sport__section-sub">As-tu une blessure ?</p>
          </div>
        </div>
        <div className="form-sport__options form-sport__options--2col">
          {[
            { id: false, emoji: '👍', label: 'Aucune' },
            { id: true, emoji: '🩹', label: 'Oui' },
          ].map((o) => (
            <button
              key={String(o.id)}
              className={`form-sport__option${blessure === o.id ? ' form-sport__option--selected' : ''}`}
              aria-pressed={blessure === o.id}
              onClick={() => {
                setBlessure(o.id);
                // Repasser sur « Aucune » doit emporter la description, sans quoi le
                // serveur recevrait « pas de blessure » avec son récit.
                if (!o.id) setDescriptionBlessure('');
              }}
            >
              <span className="form-sport__option-emoji">{o.emoji}</span>
              <span className="form-sport__option-label">{o.label}</span>
            </button>
          ))}
        </div>
        {blessure && (
          <textarea
            className="form-sport__textarea"
            placeholder="Décris ta blessure..."
            value={descriptionBlessure}
            onChange={(e) => setDescriptionBlessure(e.target.value)}
            rows={3}
          />
        )}
      </div>

      {/* ===== Courbatures =====
          Pas masquée un jour de repos, contrairement aux sections d'effort : c'est
          justement le lendemain d'une grosse séance, sans entraînement, que les
          courbatures se ressentent le plus. */}
      <div className="form-sport__section form-sport__section--col">
        <div className="form-sport__section-header">
          <img src={courbaturesImg} alt="" className="form-sport__section-icon warning" />
          <div>
            <h3 className="form-sport__section-title">Courbatures<InfoButton infoKey="formCourbatures" /></h3>
            <p className="form-sport__section-sub">De quel entraînement datent-elles ?</p>
          </div>
        </div>

        <div className="form-sport__options form-sport__options--1col">
          {COURBATURES_ONSET_OPTIONS.map((o) => (
            <button
              key={o.id}
              className={`form-sport__option${courbaturesOnset === o.id ? ' form-sport__option--selected' : ''}`}
              aria-pressed={courbaturesOnset === o.id}
              onClick={() => {
                const next = courbaturesOnset === o.id ? null : o.id;
                setCourbaturesOnset(next);
                // « Aucune » ou désélection : une intensité resterait orpheline, et le
                // backend recevrait « pas de courbatures, mais fortes ».
                if (!next || next === 'aucune') setCourbaturesLevel(null);
              }}
            >
              <span className="form-sport__option-emoji">{o.emoji}</span>
              <div className="form-sport__option-content">
                <span className="form-sport__option-label">{o.label}</span>
                <span className="form-sport__option-sub">{o.sub}</span>
              </div>
            </button>
          ))}
        </div>

        {courbaturesOnset && courbaturesOnset !== 'aucune' && (
          <div className="form-sport__options form-sport__options--3col">
            {[
              { id: 'legeres', emoji: '🙂', label: 'Légères' },
              { id: 'moderees', emoji: '😕', label: 'Modérées' },
              { id: 'fortes', emoji: '😣', label: 'Fortes' },
            ].map((o) => (
              <button
                key={o.id}
                className={`form-sport__option${courbaturesLevel === o.id ? ' form-sport__option--selected' : ''}`}
                aria-pressed={courbaturesLevel === o.id}
                onClick={() => setCourbaturesLevel(courbaturesLevel === o.id ? null : o.id)}
              >
                <span className="form-sport__option-emoji">{o.emoji}</span>
                <span className="form-sport__option-label">{o.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ===== Jour de compétition ===== */}
      <div className={`form-sport__section form-sport__section--bordered form-sport__section--competition${jourReposSportif ? ' form-sport__section--hidden' : ''}`}>
        <div className="form-sport__section-header">
          <Trophy className="form-sport__section-icon trophy" />
          <div>
            <h3 className="form-sport__section-title">Jour de compétition</h3>
            <p className="form-sport__section-sub">Compétition ou match officiel</p>
          </div>
        </div>
        <label className="form-sport__switch">
          <input type="checkbox" checked={jourCompetition} onChange={() => setJourCompetition(!jourCompetition)} />
          <span className="form-sport__switch-slider" />
        </label>
      </div>

      {/* ===== Sports pratiqués ===== */}
      <div className={`form-sport__section form-sport__section--col form-sport__section--bordered${jourReposSportif ? ' form-sport__section--hidden' : ''}`}>
        <div className="form-sport__section-header">
          <DropletG className="form-sport__section-icon" />
          <div>
            <h3 className="form-sport__section-title">Sport(s) pratiqué(s)</h3>
            <p className="form-sport__section-sub">Sélectionne ton activité du jour</p>
          </div>
        </div>
        <div className="form-sport__sport-chips">
          {(user?.sports_list?.length ? user.sports_list : SPORT_LABELS).map((label) => {
            const selected = sportsPratiques.includes(label);
            return (
              <button
                key={label}
                className={`form-sport__sport-chip${selected ? ' form-sport__sport-chip--selected' : ''}`}
                onClick={() => setSportsPratiques((prev) =>
                  selected ? prev.filter((s) => s !== label) : [...prev, label]
                )}
              >
                <span className="form-sport__sport-chip-emoji">{sportEmoji(label)}</span>
                <span className="form-sport__sport-chip-label">{label}</span>
              </button>
            );
          })}
          {/* Sports ponctuels ajoutés pour aujourd'hui seulement : ils ne sont
              pas réinjectés dans les sports du profil, mais comptent dans les
              stats et le RPE exactement comme les autres. */}
          {sportsPratiques
            .filter((s) => !(user?.sports_list?.length ? user.sports_list : SPORT_LABELS).includes(s))
            .map((label) => (
              <button
                key={label}
                className="form-sport__sport-chip form-sport__sport-chip--selected"
                onClick={() => setSportsPratiques((prev) => prev.filter((s) => s !== label))}
              >
                <span className="form-sport__sport-chip-emoji">{sportEmoji(label)}</span>
                <span className="form-sport__sport-chip-label">{label}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
          {/* « + » : ajouter une activité faite exceptionnellement aujourd'hui,
              sans l'inscrire durablement dans les sports du profil. */}
          <button
            type="button"
            className="form-sport__sport-chip form-sport__ponctuel-toggle"
            onClick={() => setShowPonctuel((v) => !v)}
            aria-expanded={showPonctuel}
          >
            <span aria-hidden="true">+</span>
            <span className="form-sport__sport-chip-label">Sport ponctuel</span>
          </button>
        </div>

        {showPonctuel && (
          <div className="form-sport__ponctuel">
            <p className="form-sport__section-sub">
              Une activité faite exceptionnellement aujourd'hui. Elle compte dans
              tes stats et ton RPE, mais ne rejoint pas tes sports habituels.
            </p>
            <div className="form-sport__sport-chips">
              {SPORT_LABELS
                .filter((label) => !sportsPratiques.includes(label))
                .filter((label) => !(user?.sports_list ?? []).includes(label))
                .map((label) => (
                  <button
                    key={label}
                    className="form-sport__sport-chip"
                    onClick={() => setSportsPratiques((prev) => [...prev, label])}
                  >
                    <span className="form-sport__sport-chip-emoji">{sportEmoji(label)}</span>
                    <span className="form-sport__sport-chip-label">{label}</span>
                  </button>
                ))}
            </div>
            <div className="form-sport__ponctuel-row">
              <input
                type="text"
                className="form-sport__input form-sport__ponctuel-input"
                placeholder="Autre sport (ex : Hyrox)"
                value={sportPonctuel}
                maxLength={40}
                onChange={(e) => setSportPonctuel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSportPonctuel();
                  }
                }}
              />
              <button
                type="button"
                className="form-sport__ponctuel-add"
                onClick={addSportPonctuel}
                disabled={!sportPonctuel.trim()}
              >
                Ajouter
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== RPE (effort ressenti) ===== */}
      <div className={`form-sport__section form-sport__section--col form-sport__section--bordered${jourReposSportif ? ' form-sport__section--hidden' : ''}`}>
        <div className="form-sport__section-header">
          <EnergyB className="form-sport__section-icon" />
          <div className="form-sport__section-title-row">
            <h3 className="form-sport__section-title">RPE (effort ressenti)<InfoButton infoKey="formRpe" /></h3>
            <span className="form-sport__slider-value" style={{ color: rpe <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense }}>{rpe}/10</span>
          </div>
        </div>
        {/* Les deux niveaux sont nommés : « clair » et « foncé » sans libellé ne
            disait rien à personne. Les teintes sont celles déjà en place
            (rpeFacile / rpeIntense), le fond en est dérivé — aucune nouvelle
            couleur n'est introduite. Le niveau courant est mis en avant. */}
        <div className="form-sport__rpe-levels">
          {[
            { label: 'Modéré', range: '1 – 5', active: rpe <= 5, color: SPORT.rpeFacile },
            { label: 'Intense', range: '6 – 10', active: rpe > 5, color: SPORT.rpeIntense },
          ].map((level) => (
            <span
              key={level.label}
              className={`form-sport__rpe-level${level.active ? ' form-sport__rpe-level--active' : ''}`}
              style={{
                background: `color-mix(in srgb, ${level.color} 18%, transparent)`,
                borderColor: level.color,
              }}
            >
              <strong>{level.label}</strong> {level.range}
            </span>
          ))}
        </div>
        <input
          type="range"
          min="1"
          max="10"
          value={rpe}
          onChange={(e) => setRpe(e.target.value)}
          className="form-sport__slider"
          style={(() => {
            const color = rpe <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense;
            const pct = (rpe - 1) / 9 * 100;
            return {
              '--rpe-color': color,
              background: `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, ${UI.sliderTrack} ${pct}%, ${UI.sliderTrack} 100%)`
            };
          })()}
        />
      </div>

      {/* ===== FC repos & FC effort ===== */}
      <div className={`form-sport__section form-sport__section--row${jourReposSportif ? ' form-sport__section--hidden' : ''}`}>
        <div className="form-sport__fc-box">
          <div className="form-sport__section-header">
            <HeartbeatB className="form-sport__section-icon" />
            <h3 className="form-sport__section-title">FC repos<InfoButton infoKey="formFrequenceCardiaque" /></h3>
          </div>
          <p className="form-sport__section-sub">Battements/min</p>
          <input
            type="text"
            className="form-sport__input"
            placeholder="80"
            value={fcRepos}
            onChange={(e) => setFcRepos(e.target.value)}
          />
        </div>
        <div className="form-sport__fc-box">
          <div className="form-sport__section-header">
            <HeartbeatB className="form-sport__section-icon" />
            <h3 className="form-sport__section-title">FC effort</h3>
          </div>
          <p className="form-sport__section-sub">Battements/min</p>
          <input
            type="text"
            className="form-sport__input"
            placeholder="125"
            value={fcEffort}
            onChange={(e) => setFcEffort(e.target.value)}
          />
        </div>
      </div>

      {/* ===== Essoufflement ===== */}
      <div className={`form-sport__section form-sport__section--col${jourReposSportif ? ' form-sport__section--hidden' : ''}`}>
        <div className="form-sport__section-header">
          <EssoufflementIcon className="form-sport__section-icon" />
          <div>
            <h3 className="form-sport__section-title">Essoufflement<InfoButton infoKey="formEssoufflement" /></h3>
            <p className="form-sport__section-sub">Comment était ta respiration ?</p>
          </div>
        </div>
        <div className="form-sport__options form-sport__options--1col">
          {[
            { id: 'pas-essoufflee', img: pasEssoufflee, label: 'Pas essoufflée', sub: 'Respiration normale' },
            { id: 'moderee', img: moderee, label: 'Modérée', sub: 'Un peu essoufflée' },
            { id: 'tres-essoufflee', img: tresEssoufflee, label: 'Très essoufflée', sub: 'Très essoufflée' },
          ].map((o) => (
            <button
              key={o.id}
              className={`form-sport__option${essoufflement === o.id ? ' form-sport__option--selected' : ''}`}
              onClick={() => setEssoufflement(essoufflement === o.id ? null : o.id)}
            >
              <img src={o.img} alt={o.label} className="form-sport__option-img" />
              <div className="form-sport__option-content">
                <span className="form-sport__option-label">{o.label}</span>
                <span className="form-sport__option-sub">{o.sub}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ===== Séances supplémentaires =====
          Deux entraînements dans la même journée sont fréquents (double
          séance, sport ponctuel en plus de l'habituel). La contrainte
          d'unicité (date, sportive) en base interdit une seconde ligne : les
          séances au-delà de la première vivent dans `sessions`. */}
      <div className={`form-sport__section form-sport__section--col${jourReposSportif ? ' form-sport__section--hidden' : ''}`}>
        {seancesSup.map((s, index) => (
          <div key={index} className="form-sport__seance">
            <div className="form-sport__seance-header">
              <h3 className="form-sport__section-title">Séance {index + 2}</h3>
              <button
                type="button"
                className="form-sport__seance-remove"
                onClick={() => setSeancesSup((prev) => prev.filter((_, i) => i !== index))}
                aria-label={`Retirer la séance ${index + 2}`}
              >✕</button>
            </div>

            <div className="form-sport__sport-chips">
              {[...new Set([...(user?.sports_list ?? []), ...s.sports])].map((label) => {
                const selected = s.sports.includes(label);
                return (
                  <button
                    key={label}
                    className={`form-sport__sport-chip${selected ? ' form-sport__sport-chip--selected' : ''}`}
                    onClick={() => updateSeance(index, {
                      sports: selected ? s.sports.filter((x) => x !== label) : [...s.sports, label],
                    })}
                  >
                    <span className="form-sport__sport-chip-emoji">{sportEmoji(label)}</span>
                    <span className="form-sport__sport-chip-label">{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="form-sport__seance-row">
              <label className="form-sport__seance-label" htmlFor={`seance-rpe-${index}`}>
                RPE <span style={{ color: s.rpe <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense, fontWeight: 600 }}>{s.rpe}/10</span>
              </label>
              <input
                id={`seance-rpe-${index}`}
                type="range"
                min="1"
                max="10"
                value={s.rpe}
                onChange={(e) => updateSeance(index, { rpe: e.target.value })}
                className="form-sport__slider"
                style={(() => {
                  const color = s.rpe <= 5 ? SPORT.rpeFacile : SPORT.rpeIntense;
                  const pct = (s.rpe - 1) / 9 * 100;
                  return {
                    '--rpe-color': color,
                    background: `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, ${UI.sliderTrack} ${pct}%, ${UI.sliderTrack} 100%)`,
                  };
                })()}
              />
            </div>

            <div className="form-sport__seance-row">
              <label className="form-sport__seance-label" htmlFor={`seance-fc-${index}`}>FC effort (bpm)</label>
              <input
                id={`seance-fc-${index}`}
                type="text"
                className="form-sport__input"
                placeholder="125"
                value={s.fcEffort}
                onChange={(e) => updateSeance(index, { fcEffort: e.target.value })}
              />
            </div>

            <div className="form-sport__seance-row">
              <label className="form-sport__seance-label" htmlFor={`seance-essouf-${index}`}>Essoufflement</label>
              <select
                id={`seance-essouf-${index}`}
                className="form-sport__input"
                value={s.essoufflement ?? ''}
                onChange={(e) => updateSeance(index, { essoufflement: e.target.value || null })}
              >
                <option value="">— non renseigné —</option>
                {Object.entries(ESSOUFFLEMENT_MAP).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>

            <label className="form-sport__seance-check">
              <input
                type="checkbox"
                checked={s.isCompetition}
                onChange={() => updateSeance(index, { isCompetition: !s.isCompetition })}
              />
              Compétition ou match officiel
            </label>
          </div>
        ))}

        {seancesSup.length < MAX_SEANCES - 1 && (
          <button
            type="button"
            className="form-sport__add-seance"
            onClick={() => setSeancesSup((prev) => [...prev, emptySeance()])}
          >
            + J'ai fait une autre séance aujourd'hui
          </button>
        )}
      </div>

      {/* ===== Note personnelle ===== */}
      <div className={`form-sport__section form-sport__section--col form-sport__section--note${jourReposSportif ? ' form-sport__section--hidden' : ''}`}>
        <div className="form-sport__section-header">
          <EnergyB className="form-sport__section-icon" />
          <h3 className="form-sport__section-title">Note personnelle (optionnel)</h3>
        </div>
        <p className="form-sport__note-sub">Notes sur ton entraînement...</p>
        <textarea
          className="form-sport__textarea form-sport__textarea--note"
          placeholder="Remarques sur la séance..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
        />
      </div>

      {/* ===== Footer Enregistrer ===== */}
      <div className="form-sport__footer">
        {error && <p style={{ color: 'var(--danger)', textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.85rem' }}>{error}</p>}
        <button className="form-sport__submit" onClick={handleSubmit}>
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default FormSport;
